import React, { useState, useEffect, useCallback } from "react";
import {
  Edit2,
  Trash2,
  Filter,
  Map,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Scale,
  Search,
  X,
  RotateCcw,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../../apiclient/apiclient";
import EditOrderModal from "./EditOrderModel";
import socket from "../../socket/socketClient";
import Pagination from "../../components/Pagination";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import OrderDateFilter from "../../components/OrderDateFilter";
import { formatAnyDateToDDMMYYYY } from "../../utils/dateFormatter";
import ThemedTable from "../../components/ThemedTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
/* ================= HELPERS ================= */

const getPaymentBadge = (status) => {
  const styles = {
    paid: "bg-green-100 text-green-700",
    partial: "bg-orange-100 text-orange-700",
    cod: "bg-gray-100 text-gray-700",
    unpaid: "bg-gray-100 text-gray-700",
  };
  return styles[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
};

const formatPaymentStatus = (status) => {
  if (!status) return "COD";
  return status.toUpperCase();
};

const getStatusBadge = (status) => {
  const styles = {
    pending: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition",
    processing: "bg-blue-100 text-orange-700",
    shipped: "bg-blue-100 text-blue-700",
    delivered: "bg-purple-100 text-purple-700",
    completed: "bg-green-100 text-green-700 hover:bg-amber-500 transition",
    cancelled: "bg-red-100 text-red-700",
  };
  return styles[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
};

const formatStatus = (status) => {
  if (!status) return "";

  switch (status) {
    case "pending":
      return "Pending";
    case "processing":
      return "Processing";
    case "shipped":
      return "Shipped";
    case "delivered":
      return "Delivered";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
};

/* ================= COMPONENT ================= */

const ViewOrders = () => {
  const currentUser = JSON.parse(
    localStorage.getItem("agent") ||
    localStorage.getItem("user") ||
    localStorage.getItem("auth") ||
    "{}"
  );
  const [orders, setOrders] = useState([]);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    completed: 0,
    cancelled: 0,
  });
  const inputClass =
    "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full text-slate-800";

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get("status");

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(statusFromUrl || "pending");
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // Live input value

  const [deleting, setDeleting] = useState(false);

  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Applied states (only these trigger the table refresh)
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  const handleDownloadInvoice = async (orderId, orderDisplayId) => {
    try {
      setDownloadingInvoiceId(orderId);
      const res = await apiClient.get(`/invoice/${orderId}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${orderDisplayId || orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      if (err.response?.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorJson = JSON.parse(reader.result);
            toast.error(errorJson.message || "Invoice not available");
          } catch (e) {
            toast.error("Invoice not available");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        toast.error(err.response?.data?.message || "Invoice not available");
      }
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  /* ---------------- SEARCH HANDLER ---------------- */
  const handleSearch = () => {
    setSearch(searchTerm);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setCurrentPage(1);
  };

  const handleDateApply = (from, to) => {
    setFromDate(from);
    setToDate(to);
  };
  const TAB_STYLES = {
    pending: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
    processing: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
    shipped: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
    delivered: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
    completed: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
    cancelled: {
      active: "bg-orange-500 text-white",
      inactive: "bg-white text-slate-600 hover:bg-slate-50",
    },
  };

  const handleDateClear = () => {
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    loadOrders(1, limit, activeTab);
  };

  const limit = 200;

  /* ================= LOAD ORDERS ================= */

  const loadOrders = useCallback(
    async (page = 1, lim = limit, status = activeTab) => {
      try {
        setLoading(true);

        let url = `/orders?page=${page}&limit=${lim}`;

        if (status) url += `&status=${status}`;
        if (appliedFromDate) url += `&from=${appliedFromDate}`;
        if (appliedToDate) url += `&to=${appliedToDate}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;

        const res = await apiClient.get(url);

        setOrders(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);

        if (res.data.statusCounts) {
          setStatusCounts(res.data.statusCounts);
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab, appliedFromDate, appliedToDate, search, limit],
  );

  // When tab, date, or limit changes, reset to page 1 and load
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [activeTab, fromDate, toDate, limit]);

  // When currentPage changes, load orders
  useEffect(() => {
    loadOrders(currentPage, limit, activeTab);
  }, [currentPage, limit, activeTab, loadOrders]);

  useEffect(() => {
    const onRealtimeEvent = () => {
      if (document.visibilityState === "visible") {
        loadOrders(currentPage, limit, activeTab);
      } else {
        setNeedsRefresh(true);
      }
    };

    socket.on("order_created", onRealtimeEvent);
    socket.on("order_deleted", onRealtimeEvent);
    socket.on("order_updated", onRealtimeEvent);
    socket.on("order_collected", onRealtimeEvent);
    socket.on("order_status_updated", onRealtimeEvent);
    socket.on("order_processed", onRealtimeEvent);
    socket.on("order_accepted", onRealtimeEvent);
    socket.on("connect", () => {
      //  loadOrders(1, limit, activeTab);
    });

    return () => {
      socket.off("order_created", onRealtimeEvent);
      socket.off("order_deleted", onRealtimeEvent);
      socket.off("order_updated", onRealtimeEvent);
      socket.off("order_collected", onRealtimeEvent);
      socket.off("order_status_updated", onRealtimeEvent);
      socket.off("order_processed", onRealtimeEvent);
      socket.off("order_accepted", onRealtimeEvent);
      socket.off("connect");
    };
  }, [currentPage, activeTab, limit, loadOrders]);

  // products
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await apiClient.get("/products");
        setProducts(
          res.data.map((p) => ({
            _id: p._id,
            productName: `${p.name} – ${p.quantityValue}${p.quantityUnit}`,
          })),
        );
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    loadProducts();
  }, []);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && needsRefresh) {
        loadOrders(currentPage, limit, activeTab);
        setNeedsRefresh(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [needsRefresh, currentPage, activeTab, limit, loadOrders]);

  /* ================= DELETE ================= */

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);

      await apiClient.delete(`/orders/${deleteModal._id}`);

      toast.success("Order deleted successfully");

      setDeleteModal(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Delete issue, refreshing...",
      );
    } finally {
      setDeleting(false);

      // ✅ ALWAYS refresh (IMPORTANT FIX)
      loadOrders(currentPage, limit, activeTab);
    }
  };

  /* ================= SEARCH ================= */

  // if (loading) {
  //   return (
  //     <div>
  //       <div className="text-gray-600">Loading orders...</div>
  //     </div>
  //   );
  // }

  /* ================= UI ================= */

  return (
    <div className="">
      {/* ================= HEADER TOP ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">View Orders</h1>
          <p className="text-sm text-slate-500 mt-1">
            All orders and their statuses
          </p>
        </div>
        <button
          onClick={() => navigate("/orders/generate-order")}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          Create Order
        </button>
      </div>

      {/* ================= FILTER BAR (PRODUCT STYLE) ================= */}

      <div className="mb-0">
        {/* ROW 1: CONTROLS */}
        <div className="flex flex-col sm:flex-row gap-2 w-full items-stretch p-4">
          {/* SEARCH */}
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              placeholder="Search by ID, Client, Product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className={`${inputClass} pl-10 pr-10`}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* DATE FILTER */}
          <div className="w-full sm:w-72">
            <OrderDateFilter
              fromDate={fromDate}
              toDate={toDate}
              onApply={handleDateApply}
              onClear={handleDateClear}
              className={inputClass}
            />
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-9 h-9 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors shadow-sm flex items-center justify-center shrink-0 disabled:opacity-60"
              title="Search"
            >
              {loading ? <Loader inline={true} text="" /> : <Search size={18} />}
            </button>

            <button
              onClick={() => {
                setSearchTerm("");
                setSearch("");
                setFromDate("");
                setAppliedFromDate("");
                setToDate("");
                setAppliedToDate("");
                setCurrentPage(1);
              }}
              disabled={loading}
              title="Reset all filters"
              className="w-9 h-9 bg-[#172554] text-white rounded-md border border-[#172554] flex items-center justify-center shrink-0 disabled:opacity-60"
            >
              <RotateCcw size={16} className={loading ? "opacity-50" : ""} />
            </button>
          </div>
        </div>

        {/* ROW 2: STATUS TABS (GRID LAYOUT FOR STRETCHED SEGMENTS) */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-[1px] bg-slate-200 border-t border-slate-200 w-full">
          {[
            ["pending", "Pending"],
            ["processing", "Processing"],
            ["shipped", "Shipped"],
            ["delivered", "Delivered"],
            ["completed", "Completed"],
            ["cancelled", "Cancelled"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setActiveTab(key);
                setCurrentPage(1);
              }}
              className={`py-2.5 px-2 flex flex-col items-center justify-center gap-1 transition-all ${activeTab === key
                  ? "bg-white text-slate-800 font-extrabold z-10"
                  : "bg-slate-50/70 text-slate-400 hover:bg-slate-100/50 hover:text-slate-600"
                }`}
            >
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${activeTab === key ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-bold ${activeTab === key
                    ? "bg-[#172554] text-white"
                    : "bg-slate-200/50 text-slate-500"
                  }`}
              >
                {statusCounts[key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-none bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <ThemedTable className="text-left border-collapse w-full">
            <thead>
              <tr className="bg-white text-gray-700 border-b border-slate-200">
                <th className="p-3 w-8"></th>
                <th className="p-3 text-left whitespace-nowrap">
                  Order Information
                </th>
                <th className="p-3 text-left whitespace-nowrap">Client</th>
                <th className="p-3 text-left whitespace-nowrap">Products</th>
                <th className="p-3 text-center whitespace-nowrap">Status</th>
                <th className="p-3 text-center whitespace-nowrap">Actions</th>
              </tr>
            </thead>

            <tbody className="text-gray-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="p-3">
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-full max-w-[120px]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-10 text-gray-400 text-sm"
                  >
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const paymentDetails = order.paymentDetails || {};
                  const calculatedTotal = order.items.reduce(
                    (sum, i) =>
                      sum + Number(i.price || 0) * Number(i.quantity || 0),
                    0,
                  );
                  const totalAmount =
                    typeof paymentDetails.totalAmount === "number"
                      ? paymentDetails.totalAmount
                      : calculatedTotal;
                  const discount =
                    typeof paymentDetails.discount === "number"
                      ? paymentDetails.discount
                      : 0;
                  const paidAmount =
                    typeof paymentDetails.paidAmount === "number"
                      ? paymentDetails.paidAmount
                      : 0;
                  const balanceAmount =
                    typeof paymentDetails.balanceAmount === "number"
                      ? paymentDetails.balanceAmount
                      : Math.max(totalAmount - discount - paidAmount, 0);
                  const paymentStatus = paymentDetails.paymentStatus || "cod";
                  const isExpanded = expandedRows.has(order._id);

                  return (
                    <React.Fragment key={order._id}>
                      {/* ── MAIN ROW ── */}
                      <tr
                        className={`border-b border-slate-100 transition cursor-pointer ${isExpanded ? "bg-blue-50" : "hover:bg-slate-50"
                          }`}
                        onClick={() => toggleRow(order._id)}
                      >
                        {/* Expand toggle */}
                        <td className="p-3 text-slate-400">
                          {isExpanded ? (
                            <ChevronDown size={16} className="text-blue-500" />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </td>

                        {/* Order Information (Merged ID + Date/Time) */}
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-800 text-sm tracking-tight">
                                {order.orderId}
                              </span>
                              {order.isBackdated && (
                                <span className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                                  Backdated
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium">
                              <span>
                                {new Date(order.createdAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    timeZone: "Asia/Kolkata",
                                  },
                                )}
                              </span>
                              <span className="text-slate-300">•</span>
                              <span>
                                {new Date(order.createdAt).toLocaleTimeString(
                                  "en-IN",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                    timeZone: "Asia/Kolkata",
                                  },
                                )}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Client (Adaptive Width) */}
                        <td className="p-3 whitespace-nowrap capitalize">
                          <div
                            className="max-w-[150px] truncate text-slate-700 font-semibold text-sm"
                            title={order.clientId?.name || "N/A"}
                          >
                            {order.clientId?.name || "N/A"}
                          </div>
                        </td>

                        {/* Products – compact list */}
                        <td className="p-3 max-w-[260px]">
                          {order.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 mb-1 last:mb-0"
                            >
                              <span className="truncate text-sm">
                                <span className="font-bold text-slate-700">{item.quantity} ×</span> {item.productName}
                              </span>
                              {item.quantityValue && item.quantityUnit && (
                                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                                  ({item.quantityValue}
                                  {item.quantityUnit})
                                </span>
                              )}
                              <span
                                className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded font-semibold whitespace-nowrap ${item.collected
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                  }`}
                              >
                                {item.collected ? "✓" : "✗"}
                              </span>
                            </div>
                          ))}
                        </td>

                        {/* Status & Amount */}
                        <td
                          className="p-3 whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex flex-col gap-1 items-center text-center">
                            <span className="text-sm font-extrabold text-slate-800 tracking-tight">
                              ₹{(totalAmount - discount).toFixed(2)}
                            </span>
                            <span
                              className={`px-2.5 py-1 rounded text-xs font-semibold ${getStatusBadge(order.status)}`}
                            >
                              {formatStatus(order.status)}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td
                          className="p-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-center gap-1.5">
                            <button
                              onClick={() => {
                                const isSuperAdmin = currentUser?.role?.toLowerCase() === "superadmin";
                                const isCompleted = order.status === "completed";
                                const isCancelled = order.status === "cancelled";

                                let viewOnly = false;
                                if (isCancelled) {
                                  viewOnly = true;
                                } else if (isCompleted && !isSuperAdmin) {
                                  viewOnly = true;
                                }

                                setEditModal(viewOnly ? { ...order, viewOnly: true } : order);
                              }}
                              title="Edit Order"
                              className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDownloadInvoice(order._id, order.orderId)}
                              title="Download Invoice"
                              disabled={downloadingInvoiceId === order._id}
                              className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition disabled:opacity-60"
                            >
                              {downloadingInvoiceId === order._id ? (
                                <Loader inline={true} text="" />
                              ) : (
                                <LiaFileInvoiceSolid size={15} />
                              )}
                            </button>
                            {order.deliveryPersonId && (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/orders/track-orders?id=${order._id}`,
                                  )
                                }
                                title="Track Order"
                                className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
                              >
                                <Map size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (
                                  order.status !== "completed" &&
                                  order.status !== "cancelled"
                                ) {
                                  setDeleteModal(order);
                                }
                              }}
                              disabled={
                                order.status === "completed" ||
                                order.status === "cancelled"
                              }
                              title={
                                order.status === "completed" ||
                                  order.status === "cancelled"
                                  ? "Cannot delete completed/cancelled orders"
                                  : "Delete order"
                              }
                              className={`w-8 h-8 flex items-center justify-center rounded-full border-2 transition ${order.status === "completed" ||
                                  order.status === "cancelled"
                                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                  : "border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500"
                                }`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* ── EXPANDED DETAIL ROW ── */}
                      {isExpanded && (
                        <tr className="border-b bg-white">
                          <td colSpan="6" className="p-0">
                            <div className="flex flex-col lg:flex-row gap-0">
                              <div className="flex-1 flex flex-col bg-white divide-y divide-slate-100">
                                  {/* Left Banner: Edge-to-edge */}
                                  <div className="bg-[#172554] text-white uppercase text-[11px] font-bold tracking-wider px-4 py-2.5 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <span>{order.orderType || "DELIVERY"} DETAILS</span>
                                      {order.isBackdated && (
                                        <span className="bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded font-black tracking-tight normal-case">
                                          Backdated
                                        </span>
                                      )}
                                    </div>
                                    <span className="opacity-95">{formatAnyDateToDDMMYYYY(order.createdAt)}</span>
                                  </div>

                                  {/* Grid Container (Split 50/50, Edge-to-Edge Columns) */}
                                  <div className="grid grid-cols-2 gap-0 divide-x divide-slate-100 text-xs">
                                    {/* Column 1: Assigned Info */}
                                    <div className="flex flex-col">
                                      <div className="bg-slate-100/70 text-slate-600 uppercase text-[10px] font-bold tracking-wider px-4 py-2 border-b border-slate-200/60">
                                        Assigned Info
                                      </div>
                                      <div className="p-4 space-y-2">
                                        <div className="flex flex-col">
                                          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">Admin</span>
                                          <span className="font-semibold text-slate-700">
                                            {order.assignedBy?.name || "N/A"}
                                          </span>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">Reference</span>
                                          <span className="font-semibold text-slate-700">
                                            {order.items?.some((i) => i.staffReference?.name)
                                              ? [...new Set(order.items.map((i) => i.staffReference?.name).filter(Boolean))].join(", ")
                                              : (order.staffReference?.name || "Direct Sales (No Agent)")}
                                          </span>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">Agent</span>
                                          <span className="font-semibold text-slate-700">
                                            {order.deliveryPersonId?.name || "N/A"}
                                          </span>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">Accepted by Agent</span>
                                          <span
                                            className={`font-bold ${order.acceptedByDeliveryBoy ? "text-green-600" : "text-amber-600"}`}
                                          >
                                            {order.acceptedByDeliveryBoy ? "Yes" : "No"}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Column 2: Financials */}
                                    <div className="flex flex-col">
                                      <div className="bg-slate-100/70 text-slate-600 uppercase text-[10px] font-bold tracking-wider px-4 py-2 border-b border-slate-200/60">
                                        Financials
                                      </div>
                                      <div className="p-4 space-y-2">
                                        <p className="text-slate-600 flex justify-between">
                                          <span>Subtotal (Excl. GST):</span>{" "}
                                          <span className="font-semibold">
                                            ₹{order.items.reduce((sum, item) => {
                                              const rate = item.productId?.taxRate ?? 0;
                                              const itemTotal = item.totalPrice || item.quantity * item.price;
                                              return sum + (itemTotal * 100 / (100 + rate));
                                            }, 0).toFixed(2)}
                                          </span>
                                        </p>
                                        <p className="text-slate-600 flex justify-between">
                                          <span>Total GST:</span>{" "}
                                          <span className="font-medium">
                                            ₹{order.items.reduce((sum, item) => {
                                              const rate = item.productId?.taxRate ?? 0;
                                              const itemTotal = item.totalPrice || item.quantity * item.price;
                                              return sum + (itemTotal * rate / (100 + rate));
                                            }, 0).toFixed(2)}
                                          </span>
                                        </p>
                                        <p className="text-slate-800 flex justify-between font-bold border-t border-slate-200/60 pt-1 mt-1">
                                          <span>Subtotal:</span>{" "}
                                          <span>
                                            ₹{Number(totalAmount).toFixed(2)}
                                          </span>
                                        </p>
                                        {(paymentDetails?.redeemedPoints > 0) && (
                                          <p className="text-slate-600 flex justify-between">
                                            <span className="text-[10px] uppercase font-bold text-orange-500">Points Redeemed ({paymentDetails.redeemedPoints}):</span>{" "}
                                            <span className="font-semibold text-orange-600">
                                              - ₹{Number(paymentDetails.pointsDiscount || 0).toFixed(2)}
                                            </span>
                                          </p>
                                        )}
                                        {(paymentDetails?.earnedPoints > 0) && (
                                          <p className="text-slate-600 flex justify-between">
                                            <span className="text-[10px] uppercase font-bold text-blue-500">Points Earned:</span>{" "}
                                            <span className="font-semibold text-blue-600">
                                              +{paymentDetails.earnedPoints}
                                            </span>
                                          </p>
                                        )}
                                        {(paymentDetails?.discount > 0 && paymentDetails?.discount !== paymentDetails?.pointsDiscount) && (
                                          <p className="text-slate-600 flex justify-between">
                                            <span className="text-[10px] uppercase font-bold text-amber-500">Discount:</span>{" "}
                                            <span className="font-semibold text-amber-600">
                                              - ₹{Number(paymentDetails.discount - (paymentDetails.pointsDiscount || 0)).toFixed(2)}
                                            </span>
                                          </p>
                                        )}
                                        {discount > 0 && (
                                          <p className="text-slate-800 flex justify-between font-bold border-t border-slate-200/40 pt-1 mt-1">
                                            <span>Net Payable:</span>{" "}
                                            <span className="text-blue-700">
                                              ₹{Number(Math.max(totalAmount - discount, 0)).toFixed(2)}
                                            </span>
                                          </p>
                                        )}

                                        <p className="text-slate-600 flex justify-between">
                                          <span>Paid:</span>{" "}
                                          <span className="font-semibold text-green-600">
                                            ₹{Number(paidAmount).toFixed(2)}
                                          </span>
                                        </p>
                                        <p className="text-slate-600 flex justify-between">
                                          <span>Balance:</span>{" "}
                                          <span
                                            className={`font-bold ${balanceAmount > 0 ? "text-red-600" : "text-green-600"}`}
                                          >
                                            ₹{Number(balanceAmount).toFixed(2)}
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Recent Transactions Section */}
                                  {paymentDetails.payments?.length > 0 && (
                                    <div className="flex flex-col">
                                      <div className="bg-slate-100/70 text-slate-600 uppercase text-[10px] font-bold tracking-wider px-4 py-2 border-b border-slate-200/60">
                                        Recent Transactions
                                      </div>
                                      <div className="p-4 flex flex-wrap gap-x-4 gap-y-2">
                                        {paymentDetails.payments.map((p, i) => (
                                          <div key={i} className="flex items-center gap-2">
                                            <span className="bg-blue-50 text-blue-700 border border-blue-100/60 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                              {p.mode}
                                            </span>
                                            <span className="font-bold text-slate-800 text-xs">
                                              ₹{Number(p.amount).toFixed(2)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {order.notes && (
                                    <div className="bg-amber-50/50 px-4 py-3 border-t border-amber-100 text-[11px] text-amber-800 italic">
                                      "{order.notes}"
                                    </div>
                                  )}
                              </div>

                              {/* Right Column: Product Detail */}
                              <div className="flex-[1.5] border-l border-slate-200/60 flex flex-col bg-white">
                                {/* Right Banner: Edge-to-edge */}
                                <div className="bg-[#172554] text-white uppercase text-[11px] font-bold tracking-wider px-4 py-2.5">
                                  Items Breakdown
                                </div>

                                {/* Right Column Content: Edge-to-Edge List */}
                                <div className="divide-y divide-slate-100 bg-white flex-1">
                                  {order.items.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between text-xs px-4 py-2.5 group hover:bg-slate-50 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full ${item.collected ? "bg-green-500" : "bg-red-400 animate-pulse"}`}
                                        />
                                        <div>
                                          <p className="font-semibold text-slate-800">
                                            {item.productName}{" "}
                                            <span className="text-slate-400 font-normal">
                                              ({item.quantityValue}
                                              {item.quantityUnit})
                                            </span>
                                          </p>
                                          <p className="text-[10px] text-slate-500">
                                            Qty: {item.quantity} × ₹{Number(item.price).toFixed(2)}{" "}
                                            • Base: ₹{(((item.totalPrice || item.quantity * item.price) * 100) / (100 + (item.productId?.taxRate ?? 0))).toFixed(2)}{" "}
                                            • GST: ₹{(((item.totalPrice || item.quantity * item.price) * (item.productId?.taxRate ?? 0)) / (100 + (item.productId?.taxRate ?? 0))).toFixed(2)}{" "}
                                            • {item.warehouseName || "No Warehouse"}
                                            {item.expiryDate && (
                                              <>
                                                {" "}• Exp: {(() => {
                                                  try {
                                                    const d = new Date(item.expiryDate);
                                                    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
                                                  } catch (e) { return "N/A"; }
                                                })()}
                                              </>
                                            )}
                                            {item.batchId && item.batchId !== "N/A" && (
                                              <> • Batch: {item.batchId}</>
                                            )}
                                            {" • "}
                                            <span className={`inline-flex items-center px-1.5 py-0.2 rounded font-semibold ${item.staffReference?.name ? "bg-purple-50 text-purple-700 border border-purple-100" : "bg-slate-100 text-slate-600"}`}>
                                              Ref: {item.staffReference?.name || order.staffReference?.name || "Direct Sales"}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-bold text-slate-800">
                                          ₹
                                          {Number(item.totalPrice ||
                                            item.quantity * item.price).toFixed(2)}
                                        </p>
                                        <span
                                          className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${item.collected ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                                        >
                                          {item.collected
                                            ? "Collected"
                                            : "Pending"}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </ThemedTable>
        </div>

        <div className="border-t border-slate-200 py-4 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* ================= MODALS (UNCHANGED) ================= */}
      {editModal && (
        <EditOrderModal
          order={editModal}
          products={products}
          onClose={() => setEditModal(null)}
          onSave={async (payload) => {
            await apiClient.put(`/orders/${payload._id}`, payload);
            setEditModal(null);
            loadOrders(currentPage, limit, activeTab);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteModal}
        title="Delete Order"
        description={`Are you sure you want to delete order ${deleteModal?.orderId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={() => setDeleteModal(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ViewOrders;
